'use client';

/**
 * SmartAppBanner — top-of-page banner suggesting the native iOS / Android
 * app to mobile visitors browsing the campsite-explorer.
 *
 * Behaviour:
 *   - Rendered with the server HTML; the <head> gate in lib/appBannerGate.ts
 *     hides it before paint where it should not show, so it never shifts
 *     the page
 *   - Detects iOS or Android via User-Agent
 *   - Only shows on mobile (md breakpoint or smaller)
 *   - Names the page: on a campsite it reads "Save <campsite> to a trip"
 *     (AppBannerName supplies the exact name; the slug stands in until then)
 *   - Hidden in iOS Safari, which shows Apple's Smart App Banner instead
 *   - Dismissible — sets a localStorage key with a 30-day TTL so we don't
 *     pester returning users
 *   - Skipped entirely when the page is loaded inside the native app's
 *     WebView (UA contains "CampingAustralia/")
 *   - Skipped on /apps/campsite-explorer/owner-portal* (operators don't
 *     need the consumer app suggestion)
 */

import { withBase } from '../lib/brand';
import React, { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { ApplePlayIcon, GooglePlayIcon } from './AppStoreBadges';
import { APP_BANNER_DISMISS_KEY } from '../lib/appBannerGate';
import { bannerCopy, campsiteSlugFromPath, isIosSafari, nameFromSlug } from '../lib/appInstall';
import { useAppBannerName } from './AppBannerName';
import { useInstallLink } from './AppInstallLink';

const DISMISS_KEY = APP_BANNER_DISMISS_KEY;
const DISMISS_DAYS = 30;
const APP_ICON_URL = withBase('/apple-touch-icon.png');

type Platform = 'ios' | 'android' | null;

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent;
  // Skip when running inside our own WebView
  if (/CampingAustralia\//.test(ua)) return null;
  // Safari shows Apple's own Smart App Banner (apple-itunes-app meta) — one
  // banner is enough.
  if (isIosSafari(ua)) return null;
  if (/iPhone|iPod|iPad/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return null;
}

function isDismissed(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const v = window.localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const until = parseInt(v, 10);
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false;
  }
}

function recordDismiss() {
  if (typeof window === 'undefined') return;
  try {
    const ms = DISMISS_DAYS * 24 * 60 * 60 * 1000;
    window.localStorage.setItem(DISMISS_KEY, String(Date.now() + ms));
  } catch {
    // ignore
  }
}

export default function SmartAppBanner() {
  const pathname = usePathname() ?? '';
  // Rendered with the server HTML (see lib/appBannerGate.ts for why).
  // Android until the effect reads the real platform: that only changes the
  // store link and a same-size icon, never the banner's height.
  const [platform, setPlatform] = useState<Exclude<Platform, null>>('android');
  const [visible, setVisible] = useState(true);
  const slug = campsiteSlugFromPath(pathname);
  const pageName = useAppBannerName();
  const copy = bannerCopy(slug ? pageName || nameFromSlug(slug) : null);
  const source = slug ? 'banner_campsite' : 'banner';
  const install = useInstallLink(source);

  // Only show on the consumer-facing campsite-explorer routes
  const onConsumerRoute =
    !pathname.includes('/owner-portal') &&
    !pathname.includes('/admin-dashboard');

  useEffect(() => {
    if (!onConsumerRoute) return;
    const p = detectPlatform();
    // The <head> gate already hid these cases before paint; this keeps
    // React's state in step with it.
    if (!p || isDismissed()) {
      setVisible(false);
      return;
    }
    setPlatform(p);
  }, [onConsumerRoute]);

  const handleDismiss = useCallback(() => {
    recordDismiss();
    setVisible(false);
  }, []);

  if (!onConsumerRoute || !visible) return null;

  const storeName = platform === 'ios' ? 'App Store' : 'Google Play';
  const label = install.opensApp ? 'Open' : 'Get';

  return (
    <div
      role="region"
      aria-label="Get the Camping Australia mobile app"
      className="smart-app-banner md:hidden bg-gradient-to-r from-sky-600 via-[#1558b0] to-indigo-700 text-white"
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        {/* App icon */}
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white/15 ring-1 ring-white/20">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={APP_ICON_URL}
            alt="Camping Australia"
            width={48}
            height={48}
            className="h-12 w-12 object-cover"
          />
        </div>

        {/* Copy */}
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-[13px] font-semibold">{copy.title}</div>
          <div className="truncate text-[11px] text-white/80">{copy.subtitle}</div>
        </div>

        {/* Open in store */}
        <a
          href={install.href}
          onClick={install.onClick}
          rel="noopener"
          className="inline-flex min-w-[76px] shrink-0 items-center justify-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[12px] font-semibold text-[#1558b0] transition hover:bg-[#e3f2fd] active:scale-[0.98]"
          aria-label={label === 'Open' ? 'Open in the app' : `Get it on ${storeName}`}
        >
          {platform === 'ios' ? (
            <ApplePlayIcon className="h-3.5 w-3.5" />
          ) : (
            <GooglePlayIcon className="h-3.5 w-3.5" />
          )}
          {label}
        </a>

        {/* Dismiss */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss app banner"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
